import {
  ABTEST_PERMISSIONS,
  CERTIFICATES_PERMISSIONS,
  COURSES_PERMISSIONS,
  GALLERY_PERMISSIONS,
  GLOSSARY_PERMISSIONS,
  JOB_PROFILE_PERMISSIONS,
  PROFILES_PERMISSIONS,
  QUIZZES_PERMISSIONS,
  SEQUENCES_PERMISSIONS,
  SKILL_MATRIX_PERMISSIONS,
} from '@/constants/permissions';
import { getDashboardRoutes } from '@/constants/routes';
import { getHasAccess } from '@/services/firebaseServices/AuthServices/helpers';
import type { UserFeatures } from '@/services/firebaseServices/AuthServices/types';
import type { NavigationConfig } from '@/types/navigation';
import { NavigationItemId, NavigationSection } from '@/types/navigation';

/**
 * Central navigation configuration - parameterized by spaceId.
 * The spaceId is embedded in all URLs so the URL is the single source of truth for space.
 *
 * Permission Logic: User must have at least ONE of the listed features to see the item
 */
export function getNavigationConfig(spaceId: string): NavigationConfig {
  const routes = getDashboardRoutes(spaceId);

  return {
    [NavigationSection.TRAINING]: [
      {
        id: NavigationItemId.CERTIFICATES,
        section: NavigationSection.TRAINING,
        titleKey: 'navigation.certificates',
        url: routes.certificate,
        icon: 'material-symbols:emoji-events',
        requiredFeatures: CERTIFICATES_PERMISSIONS,
      },
      {
        id: NavigationItemId.PROFILES,
        section: NavigationSection.TRAINING,
        titleKey: 'navigation.profiles',
        url: routes.profile.base,
        icon: 'material-symbols:group',
        requiredFeatures: PROFILES_PERMISSIONS,
      },
      {
        id: NavigationItemId.JOB_PROFILES,
        section: NavigationSection.TRAINING,
        titleKey: 'navigation.jobProfiles',
        url: routes.jobProfile.base,
        icon: 'material-symbols:workspace-premium',
        requiredFeatures: JOB_PROFILE_PERMISSIONS,
        requiredFlag: 'skillMatrixEnabled',
      },
      {
        id: NavigationItemId.SKILL_MATRIX,
        section: NavigationSection.TRAINING,
        titleKey: 'navigation.skillMatrix',
        url: routes.skillMatrix.base,
        icon: 'material-symbols:grid-on',
        requiredFeatures: SKILL_MATRIX_PERMISSIONS,
        requiredFlag: 'skillMatrixEnabled',
      },
    ],
    [NavigationSection.CONTENT]: [
      {
        id: NavigationItemId.COURSES,
        section: NavigationSection.CONTENT,
        titleKey: 'navigation.courses',
        url: routes.course.base,
        icon: 'material-symbols:menu-book',
        requiredFeatures: COURSES_PERMISSIONS,
      },
      {
        id: NavigationItemId.SEQUENCES,
        section: NavigationSection.CONTENT,
        titleKey: 'navigation.sequences',
        url: routes.sequence.base,
        icon: 'material-symbols:list',
        requiredFeatures: SEQUENCES_PERMISSIONS,
      },
      {
        id: NavigationItemId.QUIZZES,
        section: NavigationSection.CONTENT,
        titleKey: 'navigation.quizzes',
        url: routes.quiz.base,
        icon: 'material-symbols:forum',
        requiredFeatures: QUIZZES_PERMISSIONS,
      },
      {
        id: NavigationItemId.GALLERY,
        section: NavigationSection.CONTENT,
        titleKey: 'navigation.gallery',
        url: routes.gallery,
        icon: 'material-symbols:photo-library',
        requiredFeatures: GALLERY_PERMISSIONS,
      },
      {
        id: NavigationItemId.GLOSSARY,
        section: NavigationSection.CONTENT,
        titleKey: 'navigation.glossary',
        url: routes.glossary.base,
        icon: 'material-symbols:description',
        requiredFeatures: GLOSSARY_PERMISSIONS,
      },
    ],
    [NavigationSection.ADMIN]: [
      {
        id: NavigationItemId.ABTESTS,
        section: NavigationSection.ADMIN,
        titleKey: 'navigation.abTests',
        url: routes.abTest.base,
        icon: 'material-symbols:science',
        requiredFeatures: ABTEST_PERMISSIONS,
      },
    ],
  };
}

/**
 * Get all navigation items for a specific section
 */
export function getNavigationItemsBySection(spaceId: string, section: NavigationSection) {
  return getNavigationConfig(spaceId)[section];
}

/**
 * Get a specific navigation item by ID
 */
export function getNavigationItemById(spaceId: string, id: NavigationItemId) {
  const allItems = Object.values(getNavigationConfig(spaceId)).flat();
  const item = allItems.find(item => item.id === id);
  if (item === undefined) {
    throw new Error(`Navigation item with id "${id}" not found`);
  }
  return item;
}

/**
 * Get all navigation items across all sections
 */
export function getAllNavigationItems(spaceId: string) {
  return Object.values(getNavigationConfig(spaceId)).flat();
}

/**
 * Filter navigation items based on user features
 */
export function filterNavigationByFeatures(
  items: NavigationConfig[NavigationSection],
  userFeatures: UserFeatures[],
  flags: Record<string, boolean> = {},
): NavigationConfig[NavigationSection] {
  return items.filter((item) => {
    if (getHasAccess(item.requiredFeatures, userFeatures) === false) {
      return false;
    }
    if (item.requiredFlag !== undefined && flags[item.requiredFlag] !== true) {
      return false;
    }
    return true;
  });
}

/**
 * Get filtered navigation config based on spaceId and user features
 * @param spaceId - The current space ID for URL generation
 * @param userFeatures - Features the user has access to
 * @returns Navigation config with only accessible items and space-aware URLs
 */
export function getFilteredNavigationConfig(
  spaceId: string,
  userFeatures: UserFeatures[],
  flags: Record<string, boolean> = {},
): NavigationConfig {
  const config = getNavigationConfig(spaceId);
  return {
    [NavigationSection.TRAINING]: filterNavigationByFeatures(
      config[NavigationSection.TRAINING],
      userFeatures,
      flags,
    ),
    [NavigationSection.CONTENT]: filterNavigationByFeatures(
      config[NavigationSection.CONTENT],
      userFeatures,
      flags,
    ),
    [NavigationSection.ADMIN]: filterNavigationByFeatures(
      config[NavigationSection.ADMIN],
      userFeatures,
      flags,
    ),
  };
}
